import { useParams } from "wouter";
import { trpc } from "@/lib/trpc";
import { Loader2 } from "lucide-react";
import { InvoiceView } from "@/components/invoice/InvoiceView";
import { formatCardPaymentUnavailableMessage } from "@shared/invoicePayment";
import { formatGroupBillingAllocationLineDescription } from "@shared/groupBilling";

export default function DealInvoicePage() {
  const { token } = useParams<{ token: string }>();
  const { data: inv, isLoading, error } = trpc.salesDeals.getInvoiceByToken.useQuery(
    { token: token ?? "" },
    { enabled: !!token }
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !inv) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-gray-500 text-sm">Invoice not found or link has expired.</p>
          <p className="text-gray-400 text-xs mt-1">Please contact Clarity Group for assistance.</p>
        </div>
      </div>
    );
  }

  if ("inactive" in inv && inv.inactive) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <p className="text-gray-700 text-sm font-medium">
            {(inv as { inactiveMessage?: string }).inactiveMessage ??
              "This commercial invoice is no longer active."}
          </p>
          {(inv as { invoiceNumber?: string }).invoiceNumber ? (
            <p className="text-gray-400 text-xs mt-2">{(inv as { invoiceNumber: string }).invoiceNumber}</p>
          ) : null}
          <p className="text-gray-400 text-xs mt-4">Please contact Clarity Group for assistance.</p>
        </div>
      </div>
    );
  }

  const baseAmount = Number(inv.instalmentBaseAmount ?? inv.baseAmount ?? 0);
  const gstAmount = Number(inv.gstAmount ?? 0);
  const feeAmount = Number((inv as { stripeFeeAmount?: string | number | null }).stripeFeeAmount ?? 0);
  const finalAmount = Number(inv.finalAmount ?? 0);
  const cleanAmount = finalAmount > 0 ? finalAmount : Math.round((baseAmount + gstAmount) * 100) / 100;
  const paymentLink = (inv as { paymentLink?: string | null }).paymentLink ?? null;
  const picName = (inv as { salespersonName?: string | null }).salespersonName ?? null;
  const allocations = Array.isArray((inv as { allocations?: unknown }).allocations)
    ? (
        inv as {
          allocations: Array<{
            proposalRef: string;
            proposalTitle?: string | null;
            stageLabel?: string | null;
            instalmentStage?: string | null;
            allocatedAmountSgd: number;
            approvedProposalUrl?: string | null;
            billingScheduleUrl?: string | null;
          }>;
        }
      ).allocations
    : null;

  const stageLabel = (() => {
    const raw = String(inv.instalmentStage ?? "").trim();
    const pct = Number(inv.instalmentPercentage ?? 0);
    if (/^GroupBilling-\d+$/i.test(raw)) {
      return "Consolidated Group Billing";
    }
    if (/^Approval-\d+$/i.test(raw)) {
      return pct > 0 ? `Approval Payment (${pct}%)` : "Approval Payment";
    }
    return raw ? (pct > 0 ? `${raw} (${pct}%)` : raw) : "Tax invoice";
  })();

  const lineItems =
    allocations && allocations.length > 0
      ? [
          ...allocations.map((a) => {
            const description = formatGroupBillingAllocationLineDescription({
              proposalTitle: a.proposalTitle,
              proposalRef: a.proposalRef,
              stageLabel: a.stageLabel,
              instalmentStage: a.instalmentStage,
            });
            return {
              description,
              total: Number(a.allocatedAmountSgd ?? 0),
              proposalUrl: a.approvedProposalUrl ?? null,
              billingScheduleUrl: a.billingScheduleUrl ?? null,
            };
          }),
          ...(feeAmount > 0
            ? [{ description: "Card payment processing fee", total: feeAmount }]
            : []),
        ]
      : [{ description: inv.description ?? "Invoice item", total: baseAmount }];

  return (
    <InvoiceView
      vm={{
        invoiceNumber: inv.invoiceNumber ?? "Invoice",
        invoiceStatus: inv.invoiceStatus,
        invoiceDate: inv.raisedAt,
        paidAt: inv.paidAt,
        proposalRef: inv.quoteRef,
        paymentStageLabel: stageLabel,
        customerName: inv.customerName ?? "Customer",
        customerEmail: inv.customerEmail ?? null,
        picName: (inv as any).salespersonName ?? null,
        picPhone: (inv as any).picPhone ?? null,
        approvedProposalUrl: allocations?.length ? null : ((inv as any).approvedProposalUrl ?? null),
        sourceRef: inv.quoteRef,
        description: inv.description,
        lineItems,
        gstApplied: Boolean(inv.gstApplied),
        subtotal: baseAmount,
        gstAmount,
        total: cleanAmount,
        creditAppliedAmount: Number(inv.creditAppliedAmount ?? 0),
        paymentOnHold: Boolean(inv.creditOperationId),
        amountPaidInclGst: inv.invoiceStatus === "paid" && inv.paidAt ? Math.max(0, cleanAmount - Number(inv.creditAppliedAmount ?? 0)) : 0,
        amountOutstandingInclGst: inv.invoiceStatus === "paid" ? 0 : Math.max(0, cleanAmount - Number(inv.creditAppliedAmount ?? 0)),
        payNowReference: inv.invoiceNumber ?? null,
        cardPaymentLink: paymentLink,
        cardPaymentUnavailableMessage: !paymentLink
          ? formatCardPaymentUnavailableMessage(picName)
          : null,
        hasPdf: Boolean(token),
        onDownloadPdf: () => {
          if (!token) return;
          window.open(`/api/deal-invoices/${encodeURIComponent(token)}/invoice.pdf?download=1`, "_blank");
        },
        onPrintPdf: () => {
          if (!token) return;
          window.open(`/api/deal-invoices/${encodeURIComponent(token)}/invoice.pdf`, "_blank");
        },
      }}
    />
  );
}
